import ExcelJS from "exceljs";

/** Результат расчёта: объект с полями label, value, percent?, deltas?, type, inner? */
export type CalculationResultItem = {
  label?: string;
  value?: unknown;
  percent?: number;
  deltas?: number;
  type?: string;
  inner?: Array<{ label?: string; value?: unknown }>;
};

export interface ThermizationDoc {
  calculation_input?: {
    before_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
    after_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
  } | null;
  /** Дата сохранения документа */
  saved_at?: string | null;
  /** Дата расчёта */
  calculation_datetime?: string | null;
  /** Значение из справочника «Заказ-наряды» (для заголовка) */
  work_order_name?: string | null;
  /** Название линии */
  line_name?: string | null;
  /** Название смены (день/ночь и т.п.) */
  shift_label?: string | null;
  /** ФИО мастера */
  master_name?: string | null;
  /** ФИО подготовивших навески */
  prepared_by_names?: string | null;
  /** Комментарий */
  comment?: string | null;
  /** Продукт ДО */
  product_before_name?: string | null;
  /** Продукт текущий */
  product_after_name?: string | null;
  /** Вес брутто (без масла), кг */
  common_weight?: number | null;
  /** Вес поддона, кг */
  pallet_weight?: number | null;
  /** Вес переработки, кг (если есть) */
  reprocessing_weight?: number | null;
  /** Кол-во вёдер: до 00:59, от 01:00 до 05:59, ... от 48:00 (плановый простой) */
  backet_1_class?: number | null;
  backet_2_class?: number | null;
  backet_3_class?: number | null;
  backet_4_class?: number | null;
  backet_5_class?: number | null;
  backet_6_class?: number | null;
  backet_7_class?: number | null;
  /** Рассчитанные данные */
  calculation_result?: Record<string, CalculationResultItem> | null;
  /** № партии (блок Выпуск) */
  release_batch_number?: string | null;
  /** Строки таблицы Выпуск: наименование продукта, нетто кг, кол-во шт */
  release_rows?: Array<{
    product_name?: string;
    net_kg?: string | number;
    quantity_pcs?: string | number;
  }> | null;
}

/** Данные формы (для печати без сохранения / после сохранения) */
export interface ThermizationFormState {
  saveMeta: {
    calculation_datetime?: string | null;
    line_id?: { name?: string; label?: string } | null;
    line_name?: string | null;
    shift_id?: { id?: number; label?: string; name?: string } | null;
    shift_label?: string | null;
    master_employee_id?: number | { id?: number } | null;
    master_name?: string | null;
    work_order_id?: { id?: number; name?: string; label?: string } | null;
    work_order_name?: string | null;
    prepared_by_employee_ids?: unknown[];
    comment?: string | null;
  };
  input?: {
    before_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
    after_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
    common_weight?: number | null;
    pallet_weight?: number | null;
    backet_1_class?: number | null;
    backet_2_class?: number | null;
    backet_3_class?: number | null;
    backet_4_class?: number | null;
    backet_5_class?: number | null;
    backet_6_class?: number | null;
    backet_7_class?: number | null;
  } | null;
  result?: Record<string, CalculationResultItem> | null;
  releaseBatchNumber?: string;
  releaseRowsFiltered?: Array<{
    product_name?: string;
    net_kg?: number | null;
    quantity_pcs?: number | null;
  }>;
  mastersForCombobox?: Array<{ id?: number; label?: string }>;
  preparedByForCombobox?: Array<{ id?: number; label?: string }>;
}

/** Документ с обогащёнными полями (ответ getThermizationDocumentAction) */
export interface ThermizationDocumentEnriched {
  saved_at?: string | null;
  calculation_datetime?: string | null;
  line_name?: string | null;
  work_order_name?: string | null;
  shift_label?: string | null;
  master_name?: string | null;
  prepared_by_names?: string | null;
  /** Имя продукта из справочника (после getThermizationDocumentAction) */
  product_after_name?: string | null;
  comment?: string | null;
  calculation_input?: {
    before_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
    after_product?: {
      name?: string;
      base_name?: string;
      raw_material_type?: string;
    } | null;
    common_weight?: number | null;
    pallet_weight?: number | null;
    reprocessing_weight?: number | null;
    backet_1_class?: number | null;
    backet_2_class?: number | null;
    backet_3_class?: number | null;
    backet_4_class?: number | null;
    backet_5_class?: number | null;
    backet_6_class?: number | null;
    backet_7_class?: number | null;
  } | null;
  calculation_result?: Record<string, CalculationResultItem> | null;
  release_batch_number?: string | null;
  release_rows?: Array<{
    product_name?: string;
    net_kg?: string | number;
    quantity_pcs?: string | number;
  }> | null;
}

/**
 * Собирает один и тот же ThermizationDoc для печати и для скачивания из истории,
 * чтобы имя файла и заполнение полей совпадали.
 */
export function buildThermizationDoc(
  fromForm: ThermizationFormState | null,
  fromDocument: {
    document: ThermizationDocumentEnriched | null;
    row?: Record<string, unknown> | null;
  } | null,
): ThermizationDoc {
  const doc = fromDocument?.document;
  const row = fromDocument?.row ?? null;
  const sm = fromForm?.saveMeta;
  const input = fromForm?.input;
  const result = fromForm?.result;
  const releaseBatch = fromForm?.releaseBatchNumber;
  const releaseRows =
    fromForm?.releaseRowsFiltered ?? doc?.release_rows ?? null;

  const calculationDatetime =
    doc?.calculation_datetime ??
    (row as any)?.calculation_datetime ??
    sm?.calculation_datetime ??
    null;
  const lineName =
    doc?.line_name ??
    (row as any)?.line_name ??
    sm?.line_id?.name ??
    sm?.line_id?.label ??
    sm?.line_name ??
    "line";
  const shiftLabel =
    doc?.shift_label ??
    sm?.shift_label ??
    sm?.shift_id?.label ??
    sm?.shift_id?.name ??
    "";
  const masterName =
    doc?.master_name ??
    sm?.master_name ??
    (fromForm?.mastersForCombobox ?? []).find(
      (m: any) =>
        m.id === sm?.master_employee_id ||
        m.id === (sm?.master_employee_id as any)?.id,
    )?.label ??
    "";
  const workOrderName =
    doc?.work_order_name ??
    sm?.work_order_name ??
    sm?.work_order_id?.name ??
    sm?.work_order_id?.label ??
    null;
  const preparedIds = sm?.prepared_by_employee_ids;
  const preparedNames =
    doc?.prepared_by_names ??
    (fromForm?.preparedByForCombobox && Array.isArray(preparedIds)
      ? (preparedIds as any[])
          .map(
            (id: any) =>
              (fromForm.preparedByForCombobox ?? []).find(
                (p: any) => p.id === id,
              )?.label,
          )
          .filter(Boolean)
          .join(", ")
      : null);

  return {
    calculation_input: doc?.calculation_input ?? input ?? null,
    saved_at: doc?.saved_at ?? (row as any)?.saved_at ?? null,
    calculation_datetime: calculationDatetime,
    line_name: lineName,
    shift_label: shiftLabel,
    master_name: masterName,
    work_order_name: workOrderName,
    prepared_by_names: preparedNames || null,
    comment: doc?.comment ?? (row as any)?.comment ?? sm?.comment ?? null,
    product_before_name:
      doc?.calculation_input?.before_product?.name ??
      input?.before_product?.name ??
      "",
    product_after_name:
      (doc as ThermizationDocumentEnriched)?.product_after_name ??
      doc?.calculation_input?.after_product?.name ??
      (row as any)?.product_after_name ??
      input?.after_product?.name ??
      "",
    common_weight:
      doc?.calculation_input?.common_weight ?? input?.common_weight ?? null,
    pallet_weight:
      doc?.calculation_input?.pallet_weight ?? input?.pallet_weight ?? null,
    reprocessing_weight: doc?.calculation_input?.reprocessing_weight ?? null,
    backet_1_class:
      doc?.calculation_input?.backet_1_class ?? input?.backet_1_class ?? null,
    backet_2_class:
      doc?.calculation_input?.backet_2_class ?? input?.backet_2_class ?? null,
    backet_3_class:
      doc?.calculation_input?.backet_3_class ?? input?.backet_3_class ?? null,
    backet_4_class:
      doc?.calculation_input?.backet_4_class ?? input?.backet_4_class ?? null,
    backet_5_class:
      doc?.calculation_input?.backet_5_class ?? input?.backet_5_class ?? null,
    backet_6_class:
      doc?.calculation_input?.backet_6_class ?? input?.backet_6_class ?? null,
    backet_7_class:
      doc?.calculation_input?.backet_7_class ?? input?.backet_7_class ?? null,
    calculation_result: doc?.calculation_result ?? result ?? null,
    release_batch_number: releaseBatch ?? doc?.release_batch_number ?? null,
    release_rows: (() => {
      if (!releaseRows || !Array.isArray(releaseRows)) return null;
      const rows = releaseRows as Array<{
        product_name?: string;
        net_kg?: string | number | null;
        quantity_pcs?: string | number | null;
      }>;
      return rows
        .map((r) => ({
          product_name: r.product_name,
          net_kg: r.net_kg ?? undefined,
          quantity_pcs: r.quantity_pcs ?? undefined,
        }))
        .filter((r) => {
          const productName = String(r.product_name ?? "").trim();
          const hasNet = r.net_kg != null && String(r.net_kg).trim() !== "";
          const hasQty =
            r.quantity_pcs != null && String(r.quantity_pcs).trim() !== "";
          return productName !== "" || hasNet || hasQty;
        });
    })(),
  };
}

function formatDate(val: string | null | undefined): string {
  if (!val) return "";
  const raw = String(val);
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const y = d.getFullYear();
  return `${day}.${m}.${y}`;
}

function roundNumber(n: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * factor) / factor;
}

function num(val: unknown, decimals = 2): string {
  if (val === null || val === undefined) return "";
  const n = Number(val);
  if (Number.isNaN(n)) return "";
  return roundNumber(n, decimals).toFixed(decimals).replace(".", ",");
}

/** Число для ячейки Excel (тип number). */
function numExcel(val: unknown, decimals = 2): number | null {
  if (val === null || val === undefined || val === "") return null;
  const n = Number(val);
  if (Number.isNaN(n)) return null;
  return roundNumber(n, decimals);
}

function setCellNumOrEmpty(
  cell: ExcelJS.Cell,
  val: unknown,
  decimals = 2,
): void {
  const n = numExcel(val, decimals);
  cell.value = n !== null ? n : null;
}

/** Строки выпуска для шапки и итогов (как при сохранении документа). */
function filterReleaseRowsForExport(
  rows: ThermizationDoc["release_rows"],
): Array<{
  product_name?: string;
  net_kg?: string | number;
  quantity_pcs?: string | number;
}> {
  if (!rows || !Array.isArray(rows)) return [];
  return rows.filter((r) => {
    const productName = String(r.product_name ?? "").trim();
    const hasNet = r.net_kg != null && String(r.net_kg).trim() !== "";
    const hasQty =
      r.quantity_pcs != null && String(r.quantity_pcs).trim() !== "";
    return productName !== "" || hasNet || hasQty;
  });
}

function sumReleaseTotals(
  rows: ReturnType<typeof filterReleaseRowsForExport>,
): { net: string; qty: string } {
  let netSum = 0;
  let qtySum = 0;
  let anyNet = false;
  let anyQty = false;
  for (const r of rows) {
    if (r.net_kg != null && String(r.net_kg).trim() !== "") {
      netSum += Number(r.net_kg);
      anyNet = true;
    }
    if (r.quantity_pcs != null && String(r.quantity_pcs).trim() !== "") {
      qtySum += Number(r.quantity_pcs);
      anyQty = true;
    }
  }
  return {
    net: anyNet ? num(netSum) : "",
    qty: anyQty ? String(Math.round(qtySum)) : "",
  };
}

function isVerboseLogEnabled(): boolean {
  const nodeEnv = String(process.env.NODE_ENV || "").toLowerCase();
  const level = String((process.env as any).LOG_LEVEL || "").toLowerCase();
  return nodeEnv === "development" || level === "verbose" || level === "debug";
}

function toFiniteNumber(val: unknown): number | null {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function isEffectivelyZero(val: unknown): boolean {
  const n = toFiniteNumber(val);
  return n != null && Math.abs(n) < 1e-9;
}

function toExcelArgb(color: unknown): string | null {
  if (typeof color !== "string") return null;
  const cleaned = color.trim().replace(/^#/, "").toUpperCase();
  if (/^[0-9A-F]{6}$/.test(cleaned)) return `FF${cleaned}`;
  if (/^[0-9A-F]{8}$/.test(cleaned)) return cleaned;
  return null;
}

function extractCandidateColor(obj: any): string | null {
  if (!obj || typeof obj !== "object") return null;
  const candidates = [
    obj.color,
    obj.raw_material_color,
    obj.rawMaterialColor,
    obj.raw_material_type_color,
    obj.rawMaterialTypeColor,
    obj.raw_material?.color,
    obj.raw_material_type?.color,
  ];
  for (const candidate of candidates) {
    const argb = toExcelArgb(candidate);
    if (argb) return argb;
  }
  return null;
}

function normalizeRawMaterialTypeKey(val: unknown): string {
  return String(val ?? "")
    .trim()
    .toLowerCase();
}

function normalizeRawMaterialDocId(val: unknown): string | null {
  if (val == null || val === "") return null;
  if (typeof val === "string" && val.startsWith("d:")) return val;
  const raw =
    typeof val === "object" && val != null
      ? (val as any).id ?? (val as any).pk ?? val
      : val;
  if (typeof raw === "string" && raw.startsWith("d:")) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return `d:voc_raw_materials:${Math.trunc(n)}`;
}

function extractRawMaterialPosition(obj: any): number | null {
  if (!obj || typeof obj !== "object") return null;
  const pos =
    obj.raw_material_position ??
    obj.rawMaterialPosition ??
    obj.raw_material?.position ??
    obj.position;
  const n = Number(pos);
  return Number.isFinite(n) ? n : null;
}

function extractRawMaterialDocId(obj: any): string | null {
  if (!obj || typeof obj !== "object") return null;
  const candidate =
    obj.raw_materials_id ??
    obj.raw_material_id ??
    obj.raw_material?.id ??
    obj.raw_material?.pk ??
    obj.raw_material;
  return normalizeRawMaterialDocId(candidate);
}

function extractRawMaterialTypeName(obj: any): string | null {
  if (!obj || typeof obj !== "object") return null;
  const value =
    obj.raw_material_type ??
    obj.rawMaterialType ??
    obj.raw_material?.type_name ??
    obj.raw_material?.raw_material_type ??
    obj.raw_material?.name ??
    null;
  if (value == null) return null;
  const str = String(value).trim();
  return str !== "" ? str : null;
}

function applySolidFillRange(
  sheet: ExcelJS.Worksheet,
  row: number,
  colStart: number,
  colEnd: number,
  argb: string,
): void {
  for (let col = colStart; col <= colEnd; col++) {
    sheet.getCell(row, col).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb },
    };
  }
}

/** Вид сырья и подгруппа: raw_material.position * 100 + subgroup.position */
function ingredientSortScore(
  ing: Record<string, unknown>,
  row: Record<string, unknown>,
  posFromProduct: { rawPos?: unknown; subgroupPos?: unknown },
): number {
  const raw =
    ing?.raw_material_position ??
    (ing?.raw_material as { position?: unknown } | undefined)?.position ??
    row?.raw_material_position ??
    posFromProduct.rawPos;
  const sub =
    ing?.subgroup_position ??
    (ing?.subgroup as { position?: unknown } | undefined)?.position ??
    row?.subgroup_position ??
    posFromProduct.subgroupPos;
  const rawN = Number(raw);
  const subN = Number(sub);
  const r = Number.isFinite(rawN) ? rawN : 0;
  const s = Number.isFinite(subN) ? subN : 0;
  return r * 100 + s;
}

/** Тонкая граница для ячеек */
const BORDER_GRAY_ARGB = "FF808080";
const OUTER_BORDER_STYLE = "medium";

const THIN_BORDER = {
  top: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  left: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  bottom: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  right: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
};

const BOTTOM_THIN_BORDER = {
  bottom: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
};

/**
 * Строит XLSX по шаблону «Печатная форма калькулятора МЕС» / «ЗАКАЗ-НАРЯД».
 */
export async function buildThermizationWorkbook(
  doc: ThermizationDoc,
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet("ЗАКАЗ-НАРЯД", {
    pageSetup: { fitToPage: true },
  });

  sheet.getColumn(1).width = 23;
  sheet.getColumn(2).width = 23;
  sheet.getColumn(3).width = 27;
  sheet.getColumn(4).width = 13;
  sheet.getColumn(5).width = 12;

  const borderRow5 = (row: number) => {
    for (let c = 1; c <= 5; c++) {
      sheet.getCell(row, c).border = THIN_BORDER;
    }
  };
  const setLabelIndent24pt = (cell: ExcelJS.Cell) => {
    const prev = cell.alignment;
    cell.alignment = {
      ...prev,
      horizontal: "left",
      indent: 3,
    };
  };
  const setLeftAlign = (cell: ExcelJS.Cell) => {
    const prev = cell.alignment;
    cell.alignment = {
      ...prev,
      horizontal: "left",
    };
  };
  const applyFontSizeRange = (
    row: number,
    colStart: number,
    colEnd: number,
    size: number,
  ) => {
    for (let col = colStart; col <= colEnd; col++) {
      const cell = sheet.getCell(row, col);
      cell.font = {
        ...(cell.font || {}),
        size,
      };
    }
  };
  const applyOuterBorderRange = (
    rowStart: number,
    rowEnd: number,
    colStart: number,
    colEnd: number,
  ) => {
    if (rowEnd < rowStart || colEnd < colStart) return;
    for (let row = rowStart; row <= rowEnd; row++) {
      for (let col = colStart; col <= colEnd; col++) {
        const cell = sheet.getCell(row, col);
        const prev = (cell.border || {}) as any;
        cell.border = {
          ...prev,
          top:
            row === rowStart
              ? {
                  style: OUTER_BORDER_STYLE as any,
                  color: { argb: BORDER_GRAY_ARGB },
                }
              : prev.top,
          bottom:
            row === rowEnd
              ? {
                  style: OUTER_BORDER_STYLE as any,
                  color: { argb: BORDER_GRAY_ARGB },
                }
              : prev.bottom,
          left:
            col === colStart
              ? {
                  style: OUTER_BORDER_STYLE as any,
                  color: { argb: BORDER_GRAY_ARGB },
                }
              : prev.left,
          right:
            col === colEnd
              ? {
                  style: OUTER_BORDER_STYLE as any,
                  color: { argb: BORDER_GRAY_ARGB },
                }
              : prev.right,
        };
      }
    }
  };
  const verboseLogEnabled = isVerboseLogEnabled();
  const SECTION_FILL = "FFF2E9E4";
  const NET_MASS_FILL = "FFA9D08E";
  const DEFAULT_FONT_NAME = "Calibri";
  const DEFAULT_FONT_SIZE = 12;
  const CALC_FONT_SIZE = 16;
  const HIGHLIGHT_FONT_SIZE = 18;
  const CALC_ROW_HEIGHT = 24;
  const setCalcRowHeight = (row: number) => {
    sheet.getRow(row).height = CALC_ROW_HEIGHT;
  };

  let r = 1;

  const releaseFiltered = filterReleaseRowsForExport(doc.release_rows);
  const releaseForHeader = releaseFiltered.slice(0, 3);
  const releaseTotals = sumReleaseTotals(releaseFiltered);

  // «ЗАКАЗ-НАРЯД» — без рамок по строке; подчёркивание снизу у заказ-наряда (B) и № партии (E)
  sheet.getCell(r, 1).value = "ЗАКАЗ-НАРЯД";
  sheet.getCell(r, 2).value =
    doc.work_order_name != null ? String(doc.work_order_name) : "";
  sheet.getCell(r, 1).font = { bold: true };
  sheet.getCell(r, 4).value = "№ партии";
  sheet.getCell(r, 5).value =
    doc.release_batch_number != null ? String(doc.release_batch_number) : "";
  sheet.getCell(r, 2).border = BOTTOM_THIN_BORDER;
  sheet.getCell(r, 5).border = BOTTOM_THIN_BORDER;
  r += 1;
  r += 1;

  const metaRows: Array<[string, string]> = [
    [
      "Дата",
      doc.calculation_datetime ? formatDate(doc.calculation_datetime) : "",
    ],
    ["Линия", doc.line_name != null ? String(doc.line_name) : ""],
    [
      "Смена",
      doc.shift_label != null
        ? String(doc.shift_label)
            .toLowerCase()
            .includes("ночь")
          ? "ночь"
          : String(doc.shift_label)
              .toLowerCase()
              .includes("день")
          ? "день"
          : String(doc.shift_label)
        : "",
    ],
    ["Мастер", doc.master_name != null ? String(doc.master_name) : ""],
    ["", ""],
    [
      "Навески подготовил",
      doc.prepared_by_names != null ? String(doc.prepared_by_names) : "",
    ],
    ["Комментарий", doc.comment != null ? String(doc.comment) : ""],
  ];

  const metaNavesskiRowIndex = metaRows.length - 2;
  const metaCommentRowIndex = metaRows.length - 1;

  const metaStartRow = r;
  for (let i = 0; i < metaRows.length; i++) {
    const [label, val] = metaRows[i];
    sheet.getCell(r, 1).value = label;
    sheet.getCell(r, 2).value = val;
    if (label !== "" || val !== "") {
      sheet.getCell(r, 1).font = { bold: true };
    }
    sheet.getCell(r, 1).border = THIN_BORDER;
    sheet.getCell(r, 2).border = THIN_BORDER;

    if (i === 0) {
      sheet.getCell(r, 3).value = "ВЫПУСК - продукт";
      sheet.getCell(r, 4).value = "Тара";
      sheet.getCell(r, 5).value = "Кол-во, шт";
      sheet.getCell(r, 3).font = { bold: true };
      sheet.getCell(r, 4).font = { bold: true };
      sheet.getCell(r, 5).font = { bold: true };
      setLeftAlign(sheet.getCell(r, 3));
      applySolidFillRange(sheet, r, 3, 5, SECTION_FILL);
    } else if (i >= 1 && i <= 3) {
      const rr = releaseForHeader[i - 1];
      if (rr) {
        sheet.getCell(r, 3).value =
          rr.product_name != null ? String(rr.product_name) : "";
        sheet.getCell(r, 4).value =
          rr.net_kg != null && String(rr.net_kg).trim() !== ""
            ? num(rr.net_kg)
            : "";
        const qtyVal = rr.quantity_pcs;
        sheet.getCell(r, 5).value =
          qtyVal != null && String(qtyVal).trim() !== ""
            ? String(Math.round(Number(qtyVal)))
            : "";
      }
    } else if (i === metaNavesskiRowIndex) {
      sheet.getCell(r, 3).value = "Остаток с оборудования";
      sheet.getCell(r, 3).font = { italic: true };
      setLeftAlign(sheet.getCell(r, 3));
    } else if (i === metaCommentRowIndex) {
      sheet.getCell(r, 3).value = "Итого";
      sheet.getCell(r, 3).font = { bold: true };
      setLeftAlign(sheet.getCell(r, 3));
      sheet.getCell(r, 4).value = releaseTotals.net;
      sheet.getCell(r, 5).value = releaseTotals.qty;
    }
    for (let c = 3; c <= 5; c++) {
      sheet.getCell(r, c).border = THIN_BORDER;
    }
    r++;
  }
  const metaEndRow = r - 1;
  applyOuterBorderRange(metaStartRow, metaEndRow, 1, 2);
  applyOuterBorderRange(metaStartRow, metaEndRow, 3, 5);
  r += 2;

  // Исходные данные
  const sourceStartRow = r;
  sheet.mergeCells(r, 1, r, 5);
  sheet.getCell(r, 1).value = "Исходные данные";
  sheet.getCell(r, 1).font = { bold: true };
  applySolidFillRange(sheet, r, 1, 5, SECTION_FILL);
  borderRow5(r);
  r++;
  const calcForCarry = doc.calculation_result;
  const carryItem =
    calcForCarry && typeof calcForCarry === "object"
      ? (calcForCarry as Record<string, CalculationResultItem>).m_cheese_before
      : null;
  const carryoverSourceRows: Array<[string, string]> = [];
  // if (carryItem != null && typeof carryItem === "object") {
  //   const v = (carryItem as CalculationResultItem).value;
  //   carryoverSourceRows.push([
  //     "Переходящий остаток, кг",
  //     v != null && v !== "" ? num(v) : num(0),
  //   ]);
  // }

  const sourceRows = [
    [
      "Продукт ТЕКУЩИЙ",
      doc.product_after_name != null ? String(doc.product_after_name) : "",
    ],
    ...carryoverSourceRows,
    ["Вес брутто (без масла), кг", num(doc.common_weight)],
    ["Вес поддона, кг", num(doc.pallet_weight)],
  ].filter(([, val]) => String(val || "").trim() !== "");

  for (const [label, val] of sourceRows as Array<[string, string]>) {
    sheet.mergeCells(r, 1, r, 2);
    sheet.getCell(r, 1).value = label;
    setLabelIndent24pt(sheet.getCell(r, 1));
    sheet.mergeCells(r, 3, r, 5);
    sheet.getCell(r, 3).value = val;
    borderRow5(r);
    r++;
  }
  const sourceEndRow = r - 1;
  applyOuterBorderRange(sourceStartRow, sourceEndRow, 1, 5);
  // Кол-во ведер
  const bucketStartRow = r;
  sheet.mergeCells(r, 1, r, 5);
  sheet.getCell(r, 1).value = "Кол-во ведер";
  sheet.getCell(r, 1).font = { bold: true };
  applySolidFillRange(sheet, r, 1, 5, SECTION_FILL);
  borderRow5(r);
  r++;
  const bucketLabels = [
    "до 00:59",
    "от 01:00 до 05:59",
    "от 06:00 до 11:59",
    "от 12:00 до 47:59",
    "от 48:00 и более",
    "от 12:00 до 47:59 (плановый простой)",
    "от 48:00 и более (плановый простой)",
  ];
  const bucketValues = [
    doc.backet_1_class,
    doc.backet_2_class,
    doc.backet_3_class,
    doc.backet_4_class,
    doc.backet_5_class,
    doc.backet_6_class,
    doc.backet_7_class,
  ];
  for (let i = 0; i < bucketLabels.length; i++) {
    const val = bucketValues[i];
    const hasValue = val != null && Number(val) !== 0;
    if (!hasValue) continue;
    sheet.mergeCells(r, 1, r, 2);
    sheet.getCell(r, 1).value = bucketLabels[i];
    setLabelIndent24pt(sheet.getCell(r, 1));
    sheet.mergeCells(r, 3, r, 5);
    sheet.getCell(r, 3).value = num(val);
    borderRow5(r);
    r++;
  }
  const bucketEndRow = r - 1;
  applyOuterBorderRange(bucketStartRow, bucketEndRow, 1, 5);
  r += 1;

  // Рассчитанные данные — таблица (вес, гр | %)
  const calcStartRow = r;
  sheet.mergeCells(r, 1, r, 2);
  sheet.getCell(r, 1).value = "Рассчитанные данные";
  sheet.getCell(r, 3).value = "вес, гр";
  sheet.mergeCells(r, 4, r, 5);
  sheet.getCell(r, 4).value = "%";
  sheet.getCell(r, 1).font = { bold: true };
  sheet.getCell(r, 3).font = { bold: true };
  sheet.getCell(r, 4).font = { bold: true };
  applyFontSizeRange(r, 1, 5, CALC_FONT_SIZE);
  applySolidFillRange(sheet, r, 1, 5, SECTION_FILL);
  setCalcRowHeight(r);
  borderRow5(r);
  r++;

  const result = doc.calculation_result;
  if (result && typeof result === "object") {
    let netMassForPercent: number | null = null;
    const ingredientPositionByCode: Record<
      string,
      {
        rawPos?: unknown;
        subgroupPos?: unknown;
        rawTypeName?: string | null;
        fillColor?: string | null;
      }
    > = {};
    const rawMaterialTypeColorByKey = new Map<string, string>();
    const rawMaterialColorByPosition = new Map<number, string>();
    const rawMaterialColorById = new Map<string, string>();
    const missingColorTypes = new Set<string>();
    const absorbRawMaterialTypeDictionary = (dictionary: unknown) => {
      if (!dictionary) return;
      if (Array.isArray(dictionary)) {
        for (const item of dictionary as any[]) {
          const keyCandidate =
            item?.name ?? item?.label ?? item?.code ?? item?.id ?? null;
          const rawTypeKey = normalizeRawMaterialTypeKey(keyCandidate);
          const color = extractCandidateColor(item);
          if (rawTypeKey && color && !rawMaterialTypeColorByKey.has(rawTypeKey)) {
            rawMaterialTypeColorByKey.set(rawTypeKey, color);
          }
          const rawPos = extractRawMaterialPosition(item);
          if (rawPos != null && color && !rawMaterialColorByPosition.has(rawPos)) {
            rawMaterialColorByPosition.set(rawPos, color);
          }
          const rawId = extractRawMaterialDocId(item);
          if (rawId && color && !rawMaterialColorById.has(rawId)) {
            rawMaterialColorById.set(rawId, color);
          }
        }
        return;
      }
      if (typeof dictionary === "object") {
        Object.entries(dictionary as Record<string, unknown>).forEach(
          ([typeName, value]) => {
            const key = normalizeRawMaterialTypeKey(typeName);
            const color = extractCandidateColor(value) ?? toExcelArgb(value);
            if (key && color && !rawMaterialTypeColorByKey.has(key)) {
              rawMaterialTypeColorByKey.set(key, color);
            }
            const rawPos = extractRawMaterialPosition(value);
            if (rawPos != null && color && !rawMaterialColorByPosition.has(rawPos)) {
              rawMaterialColorByPosition.set(rawPos, color);
            }
            const rawId = extractRawMaterialDocId(value);
            if (rawId && color && !rawMaterialColorById.has(rawId)) {
              rawMaterialColorById.set(rawId, color);
            }
          },
        );
      }
    };
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.raw_material_types,
    );
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.raw_material_type_dict,
    );
    absorbRawMaterialTypeDictionary((doc as any)?.calculation_input?.raw_materials);
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.raw_materials_dict,
    );
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.after_product?.raw_material_types,
    );
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.after_product?.raw_material_type_dict,
    );
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.after_product?.raw_materials,
    );
    absorbRawMaterialTypeDictionary(
      (doc as any)?.calculation_input?.after_product?.raw_materials_dict,
    );
    absorbRawMaterialTypeDictionary((doc as any)?.raw_material_types);
    absorbRawMaterialTypeDictionary((doc as any)?.raw_material_type_dict);
    absorbRawMaterialTypeDictionary((doc as any)?.raw_materials);
    absorbRawMaterialTypeDictionary((doc as any)?.raw_materials_dict);

    const afterIngredients = (doc as any)?.calculation_input?.after_product
      ?.ingredients;
    if (Array.isArray(afterIngredients)) {
      for (const ing of afterIngredients) {
        const code = String((ing as any)?.code || "");
        if (!code) continue;
        const rawTypeName = extractRawMaterialTypeName(ing);
        const rawTypeKey = normalizeRawMaterialTypeKey(rawTypeName);
        const fillColor = extractCandidateColor(ing);
        if (rawTypeKey && fillColor && !rawMaterialTypeColorByKey.has(rawTypeKey)) {
          rawMaterialTypeColorByKey.set(rawTypeKey, fillColor);
        }
        const rawPos = extractRawMaterialPosition(ing);
        if (rawPos != null && fillColor && !rawMaterialColorByPosition.has(rawPos)) {
          rawMaterialColorByPosition.set(rawPos, fillColor);
        }
        const rawMaterialId = extractRawMaterialDocId(ing);
        if (rawMaterialId && fillColor && !rawMaterialColorById.has(rawMaterialId)) {
          rawMaterialColorById.set(rawMaterialId, fillColor);
        }
        ingredientPositionByCode[code] = {
          rawPos:
            (ing as any)?.raw_material_position ??
            (ing as any)?.raw_material?.position,
          subgroupPos:
            (ing as any)?.subgroup_position ?? (ing as any)?.subgroup?.position,
          rawTypeName,
          fillColor,
        };
      }
    }
    const keyToLabel: Record<string, string> = {
      sum_backet: "Общая масса, кг",
      middle_pH: "Средневзвешенная рН",
      // strach: "Крахмал",
      // strach_b152: "Крахмал THERMTEX",
      // strach_purity87: "Крахмал Purity 87",
    };

    // Масса нетто сгустков — из sum_backet.inner (или из value sum_backet)
    const sumBacket = result.sum_backet;
    if (sumBacket != null && typeof sumBacket === "object") {
      const sb = sumBacket as CalculationResultItem;
      const innerNet = sb.inner?.find(
        (x) => (x.label || "").indexOf("Масса нетто") >= 0,
      );
      const netVal = innerNet?.value ?? sb.value;
      const netMassNum = Number(netVal);
      if (Number.isFinite(netMassNum) && netMassNum > 0) {
        netMassForPercent = netMassNum;
      }
      sheet.mergeCells(r, 1, r, 2);
      sheet.getCell(r, 1).value = "Масса нетто сгустков, кг";
      sheet.getCell(r, 1).font = { bold: true };
      setCellNumOrEmpty(sheet.getCell(r, 3), netVal);
      sheet.mergeCells(r, 4, r, 5);
      sheet.getCell(r, 4).value = "";
      applyFontSizeRange(r, 1, 5, HIGHLIGHT_FONT_SIZE);
      applySolidFillRange(sheet, r, 1, 2, NET_MASS_FILL);
      setCalcRowHeight(r);
      borderRow5(r);
      r++;
      // // «Крахмал (общий)» = тому же значению, что и строка «Крахмал» (result.strach.value)
      // const starchMain =
      //   result?.strach?.value != null ? Number(result.strach.value) : NaN;
      // const starchThermtex = result?.strach_b152?.value != null ? Number(result.strach_b152.value) : 0;
      // const starchPurity = result?.strach_purity87?.value != null ? Number(result.strach_purity87.value) : 0;
      // const starchTotal = !Number.isNaN(starchMain)
      //   ? starchMain
      //   : starchThermtex + starchPurity;
      // sheet.mergeCells(r, 1, r, 2);
      // sheet.getCell(r, 1).value = "Крахмал (общий)";
      // sheet.getCell(r, 1).font = { bold: true };
      // sheet.getCell(r, 3).value = num(starchTotal);
      // sheet.mergeCells(r, 4, r, 5);
      // sheet.getCell(r, 4).value = "";
      // borderRow5(r);
      // r++;
    }

    // Средневзвешенная рН
    const middlePh = result.middle_pH;
    if (middlePh != null && typeof middlePh === "object") {
      const val = (middlePh as CalculationResultItem).value;
      sheet.mergeCells(r, 1, r, 2);
      sheet.getCell(r, 1).value = "Средневзвешенная рН";
      sheet.getCell(r, 1).font = { bold: true };
      // Текст, не number — чтобы сумма по столбцу массы не включала рН.
      const phDisplay = num(val);
      sheet.getCell(r, 3).value = phDisplay !== "" ? phDisplay : null;
      sheet.mergeCells(r, 4, r, 5);
      sheet.getCell(r, 4).value = "";
      applyFontSizeRange(r, 1, 5, CALC_FONT_SIZE);
      setCalcRowHeight(r);
      borderRow5(r);
      r++;
    }

    // Остальные ингредиенты из result (кроме уже выведенных)
    const ingredientRows = Object.entries(result)
      .filter(
        ([key, item]) =>
          key !== "m_cheese_before" &&
          key !== "sum_backet" &&
          key !== "middle_pH" &&
          item != null &&
          typeof item === "object",
      )
      .map(([key, item]) => {
        const row: any = item;
        const ing = row?.ingredient || {};
        const posFromProduct = ingredientPositionByCode[key] || {};
        const rawTypeName =
          extractRawMaterialTypeName(ing) ??
          extractRawMaterialTypeName(row) ??
          posFromProduct.rawTypeName ??
          null;
        const rawMaterialId =
          extractRawMaterialDocId(ing) ??
          extractRawMaterialDocId(row) ??
          null;
        const rawPos =
          extractRawMaterialPosition(ing) ??
          extractRawMaterialPosition(row) ??
          (() => {
            const n = Number(posFromProduct.rawPos);
            return Number.isFinite(n) ? n : null;
          })();
        const rawTypeKey = normalizeRawMaterialTypeKey(rawTypeName);
        const fillColor =
          extractCandidateColor(ing) ??
          extractCandidateColor(row) ??
          posFromProduct.fillColor ??
          (rawMaterialId ? rawMaterialColorById.get(rawMaterialId) ?? null : null) ??
          (rawPos != null ? rawMaterialColorByPosition.get(rawPos) ?? null : null) ??
          (rawTypeKey ? rawMaterialTypeColorByKey.get(rawTypeKey) ?? null : null);
        if (!fillColor && rawTypeName) {
          missingColorTypes.add(rawTypeName);
        }
        return {
          key,
          label: keyToLabel[key] || row.label || key,
          val: row.value,
          pct: row.percent,
          sortScore: ingredientSortScore(ing, row, posFromProduct),
          fillColor,
          rawTypeName,
          hiddenByZero: isEffectivelyZero(row.value),
        };
      })
      .sort((a, b) => {
        if (a.sortScore !== b.sortScore) return a.sortScore - b.sortScore;
        return String(a.label).localeCompare(String(b.label), "ru");
      });
    const totalIngredientRows = ingredientRows.length;
    const visibleIngredientRows = ingredientRows.filter((item) => !item.hiddenByZero);
    const hiddenByZeroCount = totalIngredientRows - visibleIngredientRows.length;
    if (verboseLogEnabled) {
      console.debug("[thermizationXlsx] ingredient rows filtered", {
        totalIngredientRows,
        hiddenByZeroCount,
        visibleIngredientRows: visibleIngredientRows.length,
      });
    }
    if (missingColorTypes.size > 0) {
      console.warn("[thermizationXlsx] missing raw material type color", {
        types: Array.from(missingColorTypes).sort((a, b) =>
          a.localeCompare(b, "ru"),
        ),
      });
    }

    for (const item of visibleIngredientRows) {
      sheet.mergeCells(r, 1, r, 2);
      const ingredientLabelCell = sheet.getCell(r, 1);
      ingredientLabelCell.value = item.label;
      ingredientLabelCell.font = {
        ...(ingredientLabelCell.font || {}),
        bold: true,
      };
      setLabelIndent24pt(sheet.getCell(r, 1));
      setCellNumOrEmpty(sheet.getCell(r, 3), item.val);
      sheet.mergeCells(r, 4, r, 5);
      setCellNumOrEmpty(sheet.getCell(r, 4), item.pct, 3);
      if (item.fillColor) {
        applySolidFillRange(sheet, r, 1, 2, item.fillColor);
      }
      applyFontSizeRange(r, 1, 5, CALC_FONT_SIZE);
      setCalcRowHeight(r);
      if (verboseLogEnabled) {
        console.debug("[thermizationXlsx] ingredient row color", {
          key: item.key,
          label: item.label,
          rawTypeName: item.rawTypeName ?? null,
          fillColor: item.fillColor ?? null,
        });
      }
      borderRow5(r);
      r++;
    }

    sheet.mergeCells(r, 1, r, 2);
    sheet.getCell(r, 1).value = "Милкстаб";
    sheet.getCell(r, 1).font = { bold: true };
    setLabelIndent24pt(sheet.getCell(r, 1));
    sheet.getCell(r, 3).value = "";
    sheet.mergeCells(r, 4, r, 5);
    sheet.getCell(r, 4).value = "";
    applyFontSizeRange(r, 1, 5, CALC_FONT_SIZE);
    setCalcRowHeight(r);
    borderRow5(r);
    r++;

    // Переработка, кг — внизу блока ингредиентов
    if (
      doc.reprocessing_weight != null &&
      String(doc.reprocessing_weight).trim() !== ""
    ) {
      const reprocessingNum = Number(doc.reprocessing_weight);
      const reprocessingPercent =
        Number.isFinite(reprocessingNum) &&
        netMassForPercent != null &&
        netMassForPercent > 0
          ? (reprocessingNum / netMassForPercent) * 100
          : null;
      sheet.mergeCells(r, 1, r, 2);
      sheet.getCell(r, 1).value = "Переработка, кг";
      sheet.getCell(r, 1).font = { bold: true };
      setLabelIndent24pt(sheet.getCell(r, 1));
      setCellNumOrEmpty(sheet.getCell(r, 3), doc.reprocessing_weight);
      sheet.mergeCells(r, 4, r, 5);
      setCellNumOrEmpty(sheet.getCell(r, 4), reprocessingPercent);
      applyFontSizeRange(r, 1, 5, CALC_FONT_SIZE);
      setCalcRowHeight(r);
      borderRow5(r);
      r++;
    }
  }

  sheet.mergeCells(r, 1, r, 2);
  sheet.getCell(r, 1).value = "Общая масса, кг";
  sheet.getCell(r, 1).font = { bold: true };
  const totalMass =
    result?.sum_backet != null && typeof result.sum_backet === "object"
      ? (result.sum_backet as CalculationResultItem).value
      : null;
  sheet.mergeCells(r, 3, r, 5);
  setCellNumOrEmpty(sheet.getCell(r, 3), totalMass);
  applyFontSizeRange(r, 1, 5, HIGHLIGHT_FONT_SIZE);
  borderRow5(r);
  r++;
  const calcEndRow = r - 1;
  applyOuterBorderRange(calcStartRow, calcEndRow, 1, 5);

  sheet.eachRow({ includeEmpty: false }, (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      const prev = cell.alignment;
      const prevFont = cell.font;
      const cellAddress = String((cell as any)?.address ?? "");
      const isColumnA = /^A\d+$/i.test(cellAddress);
      const rawVal = cell.value as any;
      const cellText =
        rawVal && typeof rawVal === "object" && Array.isArray(rawVal.richText)
          ? rawVal.richText
              .map((part: any) => String(part?.text ?? ""))
              .join("")
              .trim()
          : String(rawVal ?? "").trim();
      const forceLeft =
        isColumnA ||
        cellText === "ВЫПУСК - продукт" ||
        cellText === "Остаток с оборудования" ||
        cellText === "Итого";
      cell.alignment = {
        horizontal: forceLeft ? "left" : (prev?.horizontal ?? "center"),
        vertical: prev?.vertical ?? "middle",
        wrapText: prev?.wrapText,
        indent: prev?.indent,
        readingOrder: prev?.readingOrder,
        textRotation: prev?.textRotation,
      };
      cell.font = {
        ...(prevFont || {}),
        name: prevFont?.name ?? DEFAULT_FONT_NAME,
        size: prevFont?.size ?? DEFAULT_FONT_SIZE,
      };
    });
  });

  try {
    const buffer = await wb.xlsx.writeBuffer();
    return buffer as ArrayBuffer;
  } catch (err) {
    // Preserve original error message but add context for easier debugging.
    console.debug("[thermizationXlsx] writeBuffer error", err);
    throw err;
  }
}

/**
 * Имя файла для выгрузки: {дата}_{название линии}.xlsx
 * - дата: дата расчёта (DD-MM-YYYY)
 * - название линии: название выбранной линии в поле «Линия»
 */
export function thermizationFilename(doc: ThermizationDoc): string {
  const dateSource = doc.saved_at ?? doc.calculation_datetime;
  const datePart = dateSource
    ? formatDate(dateSource).replace(/\./g, "-")
    : "report";
  const linePart =
    (doc.line_name != null && String(doc.line_name).trim() !== ""
      ? String(doc.line_name).trim()
      : "line"
    )
      .replace(/[^\w\u0400-\u04FF\s-]/g, "")
      .replace(/\s+/g, "_")
      .replace(/-+/g, "-")
      .replace(/_+/g, "_") || "line";
  return `${datePart}_${linePart}.xlsx`;
}
